import { Injectable, EventEmitter, Output, Directive } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';

import { IUser, AUTH_QUERY_LOGIN, IAuthentication, ContactExistsDetailsQuery, IContactExists, ApiService, ILogin } from '@ebb-v2/ebb-integration';
import { Apollo } from 'apollo-angular';
import { LocationStrategy } from '@angular/common';
import { environment } from '../../environments/environment';

export const HOMEOWNER_TYPE = 'PROPERTY OWNER';
export const CONTRACTOR_TYPE = 'CONTRACTOR';
export const ENGINEER_TYPE = 'ENGINEER';
export const INSPECTOR_TYPE = 'INSPECTOR';

@Directive()
@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    @Output() getCurrentUserName: EventEmitter<any> = new EventEmitter();
    @Output() getCurrentUser: EventEmitter<any> = new EventEmitter();
    private uriApi: string;
    private uriSpaRedirectAuthorization: string;
    private programTypeCodeOptionSet: string;
    private isProgramCeaBb: boolean;
    private _isProgramEss: boolean;
    private _isProgramEbb: boolean;
    private spaBaseUrl: string;
    private uriSpaRedirectLogin: string;
    private uriSpaRegistration: string;
    private headerContentType = new HttpHeaders({'Content-Type':'application/json'});
    private currentUserSubject: BehaviorSubject<IUser>;

    public currentUser: Observable<IUser>;
    public redirectUrl: string;


    constructor(
      private router: Router,
      private locationStrategy: LocationStrategy,
      private apollo: Apollo,
      private http: ApiService,
      private httpClient: HttpClient,
    ) {
        if (window['ebbApi']) { //set by hosted site
          this.uriApi = window['ebbApi'];
          this.uriSpaRegistration = '/our-seismic-retrofit-programs/how-to-register'; //link to reg spa
        } else {
          this.uriApi = `https://${environment.devBaseUrl}/api`;
          this.uriSpaRedirectAuthorization = 'http://localhost:4577/authorization';
          this.uriSpaRedirectLogin = 'http://localhost:4577/login';
          this.uriSpaRegistration = 'http://localhost:4200/register/questionnaire';
        }
        
        if(window['spaBaseUrl']) {
          this.spaBaseUrl = window['spaBaseUrl'];
        } else {
          const baseurl = window.location.origin;
          this.spaBaseUrl = `${baseurl}${this.locationStrategy.getBaseHref()}`;
        }

        this.uriSpaRedirectAuthorization =  `${this.spaBaseUrl}/authorization`;
        this.uriSpaRedirectLogin = `${this.spaBaseUrl}/login`;

        //program used for B2C policy selection
        const programLocalStore = localStorage.getItem('ebbprogram');
        if (window['program']) {
          this.programTypeCodeOptionSet = window['program'];
        } else if (programLocalStore) {
          this.programTypeCodeOptionSet = programLocalStore;
        } else {
          this.programTypeCodeOptionSet = 'FEMA|EBB'; //default to EBB programs
        }
        const programsSplit = this.programTypeCodeOptionSet.split('|');
        this.isProgramCeaBb = programsSplit.filter(program => program === 'CEA BB').length >= 1;
        this._isProgramEss = programsSplit.filter(program => program === 'ESS').length >= 1;
        this._isProgramEbb = !(this.isProgramCeaBb || this._isProgramEss);

        //set current user
        this.currentUserSubject = new BehaviorSubject<IUser>(JSON.parse(localStorage.getItem('currentUser')));
        this.currentUser = this.currentUserSubject.asObservable();
    }

    public get uriSpaRegister(): string {
        return this.uriSpaRegistration;
    }

    public get programType(): string {
      return this.programTypeCodeOptionSet;
    }

    public isProgramCea() : boolean {
      return this.isProgramCeaBb;
    }

    public isProgramEss(): boolean {
      return this._isProgramEss;
    }

    public isProgramEbb(): boolean {
      return this._isProgramEbb;
    }

    public get currentUserValue(): IUser {
      return this.currentUserSubject.value;
    }

    public setCurrentUserValue(user: IUser) {
        if (user) {
            localStorage.setItem('dashboard', user.mode);
            this.currentUserSubject.next(user);
            this.getCurrentUserName.emit(user.fullName);
            this.getCurrentUser.emit(user);
        }
    }

    public isMultiUser(user: IUser)  {
      if(user)  {
        if(user.isContractor && user.isHomeowner)  {
          return true;
        }
      }

      return false;
    }

    public defaultUserMode(user: IUser)  {
      if (this.isMultiUser(user)) {
        const dashboard = localStorage.getItem('dashboard');
        return (dashboard === CONTRACTOR_TYPE || dashboard == HOMEOWNER_TYPE) ? dashboard : HOMEOWNER_TYPE;
      }

      if(user.isHomeowner === true)  {
        return HOMEOWNER_TYPE;
      }

      if(user.isContractor === true)  {
        return CONTRACTOR_TYPE;
      }

      if(user.isEngineer === true)  {
        return ENGINEER_TYPE;
      }

      if(user.isInspector === true)  {
        return INSPECTOR_TYPE;
      }

      return '';
    }

    public isHomeowner(user: IUser)  {
      return user && user.isHomeowner === true && user.mode === HOMEOWNER_TYPE;
    }

    public isContractor(user: IUser)  {
      return  user && user.isContractor === true && user.mode === CONTRACTOR_TYPE;
    }

    public isEngineer(user: IUser)  {
      return  user && user.isEngineer === true && user.mode === ENGINEER_TYPE;
    }

    public isInspector(user: IUser)  {
      return  user && user.isInspector === true && user.mode === INSPECTOR_TYPE;
    }

    public loginUser(loginHint: string, redirectEndpoint?: string) : string {
        const nonce: string = Math.random().toString(36).substring(2, 4) + Math.random().toString(36).substring(2, 4);
        let redirectUrl= this.redirectUrl ? this.spaBaseUrl + this.redirectUrl : this.uriSpaRedirectAuthorization;
        if(redirectEndpoint)  {
          redirectUrl = redirectEndpoint;
        }
        let redirect = `${this.uriApi}/Authenticate/login?nonce=${nonce}&redirect=true&redirectEndpoint=${encodeURIComponent(redirectUrl)}&useCeaPolicy=${this.isProgramCeaBb}`;
        if(loginHint){
            redirect += `&loginHint=${encodeURIComponent(loginHint)}`;
        }

        return redirect;
    }

    public loginCrossSiteUser(siteApi: string, loginHint: string, redirectEndpoint?: string) : string {
      const nonce: string = Math.random().toString(36).substring(2, 4) + Math.random().toString(36).substring(2, 4);

      let redirect = `${siteApi}/Authenticate/login?nonce=${nonce}&redirect=true&redirectEndpoint=${encodeURIComponent(redirectEndpoint)}&useCeaPolicy=${this.isProgramCeaBb}`;
        if(loginHint){
            redirect += `&loginHint=${encodeURIComponent(loginHint)}`;
        }

        return redirect;
    }

    public forgotPasswordRedirectUser(loginHint: string) : string {
        const nonce: string = Math.random().toString(36).substring(2, 4) + Math.random().toString(36).substring(2, 4);
        let redirect = `${this.uriApi}/Authenticate/forgotpassword?nonce=${nonce}&redirect=true&redirectEndpoint=${encodeURIComponent(this.uriSpaRedirectAuthorization)}&useCeaPolicy=${this.isProgramCeaBb}`;
        if(loginHint){
            redirect += `&loginHint=${encodeURIComponent(loginHint)}`;
        }

        return redirect;
    }

    public loginGuest(guestInfo: ILogin, redirectEndpoint: string): Observable<any> {
      
      let url = `${this.uriApi}/Authenticate/loginGuest?email=${encodeURIComponent(guestInfo.email)}` +
      `&firstName=${encodeURIComponent(guestInfo.firstName)}&lastName=${encodeURIComponent(guestInfo.lastName)}` +
      `&nonce=${encodeURIComponent(guestInfo.nonce)}`;

      if(redirectEndpoint){
        url += `&redirectEndpoint=${encodeURIComponent(this.spaBaseUrl + redirectEndpoint)}&=useCeaPolicy=${this.isProgramCeaBb}`+
                  `&b2cRedirectEndpoint=${encodeURIComponent(this.spaBaseUrl)}`;
        window.location.href = url;
        return of(false);
      }

      return this.http
      .get(`/Authenticate/loginGuest?email=${encodeURIComponent(guestInfo.email)}&firstName=${encodeURIComponent(guestInfo.firstName)}&lastName=${encodeURIComponent(guestInfo.lastName)}&nonce=${encodeURIComponent(guestInfo.nonce)}`)
        .pipe(
          map(data => data as ILogin)
        );
    }

    public logoutUser() : string {
        localStorage.removeItem('currentUser');
        this.currentUserSubject.next(null);
        const redirect = `${this.uriApi}/Authenticate/logout?redirectEndpoint=${this.uriSpaRedirectLogin}`;
        return redirect;
    }

    public getCurrentAuthentication() : Observable<IAuthentication> {
        return this.apollo.query({
            query: AUTH_QUERY_LOGIN
          })
          .pipe(map(result => result.data['authentication'] as IAuthentication));
    }
   
    public getUserStatus(email: string,recaptchaToken:string) : Observable<IContactExists>{
        return this.apollo.query({
            query: ContactExistsDetailsQuery,
            fetchPolicy: 'network-only',
            variables: { email: email,recaptchaToken:recaptchaToken}
          })
          .pipe(map(result => {
            if(result.data['message']) {
              return result.data['message'];
            }
            if (result.errors && result.errors.length > 0) {
              var message = result.errors['message'] as string;
              var error: IContactExists = { message: message };
             
              return error;
            }
            return result.data['contactExistsDetail'] as IContactExists;
          }
        ));
    }

    public convertLegacyEbb(email: string, password: string) : Observable<string>{
        return this.http.post(`/Authenticate/createAccountFromEbbLegacy`,
            {
                email: email,
                password: password
            })
            .pipe(
                map(result => result.message as string)
            );
    }

    public convertLegacyForgotPasswordEbb(email: string) : Observable<string>{
        return this.http.post(`/Authenticate/createAccountFromEbbLegacyForgot`,
            {
                email: email
            })
            .pipe(
                map(result => result.message as string)
            );
    }
}
